import DOMPurify from "dompurify";
import { type CommentBodyTocEntry, getToc } from "./commentBody.util.heading";
import { type NoteData, transformNotes } from "./commentBody.util.notes";
import type { KohaMentionItem } from "~/components/commentNodeKoha.types";
import type { CommentBodyPartInfo } from "~/components/commentBody.util.types";
import {
  addHeadingClassCorrespondingLevels,
  shiftHeadingLevels,
} from "~/units/dom";
import {
  ARGUMENT_DATA_TAG,
  transformList,
  transformNodeToCommentBodyPartCollapseInfo,
} from "~/components/commentBody.util.collapsibleLists";

export type CommentBodyInfo = {
  /**
   * Data for the table of contents.
   */
  toc: CommentBodyTocEntry[];

  /**
   * Whether the body contains an invalid heading structure.
   */
  hasHeadingError: boolean;

  /**
   * Data for the notes (bibliography).
   */
  notes: NoteData[];

  /**
   * The optional summary body template markup.
   */
  bodySummary: string;

  /**
   * Separate parts of the full comment body.
   */
  bodyFullParts: Array<CommentBodyPartInfo>;
};

/**
 * TODO: We can (and should) do most of this on the back end.
 */
export function prepareCommentBody(
  window: Window & typeof globalThis,
  nid: number,
  // TODO: Remove `kohaMentionItems` after `lw_mention_*` fields have been inlined into the body field.
  kohaMentionItems: KohaMentionItem[],
  bodyMarkupFull: string,
  bodyMarkupSummary: string,
  headingStartLevel: number,
): CommentBodyInfo {
  const purify = DOMPurify(window);

  // Since we will display the HTML from the back end to the user, we must ensure it is free from nasty injections.
  const body = purify.sanitize(bodyMarkupFull, {
    RETURN_DOM: true,
  });

  if (!(body instanceof window.HTMLElement)) {
    throw new Error("Invalid comment body markup");
  }

  const notes = transformNotes(window, body, nid, kohaMentionItems);

  transformList(window, body);

  const { toc, hasHeadingError } = getToc(body);

  addHeadingClassCorrespondingLevels(body);

  // The content in Drupal always starts with h1, but we want to start with a lower level, depending on where
  // in the site we place the content. Here we need to start with h2.
  shiftHeadingLevels(window, body, headingStartLevel);

  return {
    toc,
    hasHeadingError,
    notes,
    bodyFullParts: splitBodyIntoParts(window, body),
    bodySummary: purify.sanitize(bodyMarkupSummary),
  };
}

/**
 * Split the comment body into sections like "HTML" and "COLLAPSE".
 *
 * This only works on the top level of the HTML, but it's
 * much faster than shipping the entire Vue Runtime Compiler.
 *
 * @param window
 *   The window object. In a browser, just use `window`. In Node, use something like JSDOM.
 * @param body
 *   The body DOM.
 */
function splitBodyIntoParts(
  window: Window & typeof globalThis,
  body: HTMLElement,
): Array<CommentBodyPartInfo> {
  return [...body.childNodes]
    .filter((node: ChildNode) => {
      return (
        node instanceof window.Element ||
        (node instanceof window.Text && !!node.wholeText.trim())
      );
    })
    .map((a: any): Element | Text => a)
    .map((node: Element | Text, index): CommentBodyPartInfo => {
      if (node instanceof window.Text) {
        return {
          type: "HTML",
          tag: "SPAN",
          attributes: {},
          innerHTML: node.wholeText,
          key: index,
        };
      }
      if (node.getAttribute(ARGUMENT_DATA_TAG)) {
        return {
          type: "COLLAPSE",
          data: transformNodeToCommentBodyPartCollapseInfo(node),
          key: index,
        };
      }

      return {
        type: "HTML",
        tag: node.tagName,
        attributes: [...node.attributes].reduce((acc, attr) => {
          return { ...acc, [attr.name]: attr.value };
        }, {}),
        innerHTML: node.innerHTML.trim(),
        key: index,
      };
    });
}
